(() => {
  // =========================
  // HELPERS
  // =========================
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const norm = (s) => (s || "").toString().trim().toLowerCase();
  const money = (cents) =>
    (Number(cents) / 100).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

  const todayStr = () => {
    const d = new Date();
    return d.toLocaleDateString("it-IT", { year: "numeric", month: "2-digit", day: "2-digit" });
  };

  async function fetchProduct(handle) {
    const res = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
    if (!res.ok) throw new Error("Prodotto non trovato: " + handle);
    return await res.json();
  }

  // =========================
  // INIT
  // =========================
  document.addEventListener("DOMContentLoaded", async () => {
    const root = $(".seabob-configurator");
    if (!root) return;

    // CONFIG from data-attributes
    const CONFIG = {
      f9Handle: root.dataset.f9Handle || "seabob-f9",
      f9sHandle: root.dataset.f9sHandle || "seabob-f9s",
      whatsappNumber: (root.dataset.whatsappNumber || "").replace(/\D/g, "") || "393423488673",
      colorOptionIndex: Number(root.dataset.colorOptionIndex || 1)
    };

    const state = {
      model: "f9",
      product: null,
      products: { f9: null, f9s: null },
      colorLabel: null,
      variantId: null,
      variant: null,
      galleryImages: [],
      galleryActive: null,
      accessories: [], // {handle, product, checked}
      discount: { unlockedUntil: 0, mode: "percent", value: 0 },
    };

    // Static items for quote (IVA inclusa 22%). These are selectable for the quote even if not on Shopify.
    const QUOTE_ITEMS = [
      {
        key: "special-colour",
        category: "Optional",
        title: "Colore speciale",
        desc: "Colori metallizzati esclusivi lucidi con sistema di verniciatura multistrato.",
        grossCents: 56730,
      },
      {
        key: "black-package",
        category: "Optional",
        title: "Black Package",
        desc: "Anello di poppa e cornice console centrale in design sportivo nero opaco.",
        grossCents: 0,
      },
      {
        key: "chrome-package",
        category: "Optional",
        title: "Chrome Package",
        desc: "Anello di poppa e cornice console centrale in finitura cromata.",
        grossCents: 21716,
      },
      {
        key: "colour-package",
        category: "Optional",
        title: "Colour Package",
        desc: "Anello di poppa e cornice console centrale in tinta scafo (colore carrozzeria).",
        grossCents: 21716,
      },
      {
        key: "battery-capacity-plus",
        category: "Optional",
        title: "Battery Capacity Plus",
        desc: "Capacità batteria estesa per un tempo di utilizzo più lungo.",
        grossCents: 87596,
      },
      {
        key: "driving-stability-system",
        category: "Optional",
        title: "Driving Stability System",
        desc: "Ali posteriori estendibili con cinematismo per maggiore stabilità.",
        grossCents: 50630,
      },

      {
        key: "quick-charger",
        category: "Accessori",
        title: "Quick Charger",
        desc: "Per tempi di ricarica più rapidi.",
        img: "https://seabob.com/media/F9_charger.png.webp",
        grossCents: 181170,
      },
      {
        key: "seabob-bag",
        category: "Accessori",
        title: "SEABOB Bag",
        desc: "Borsa per trasporto e viaggi.",
        img: "https://seabob.com/media/SEABOB_F9_Bag.jpg.webp",
        grossCents: 36356,
      },
      {
        key: "seabob-rack",
        category: "Accessori",
        title: "SEABOB Rack",
        desc: "Sistema di fissaggio sicuro a parete e pavimento per il tuo SEABOB.",
        img: "https://seabob.com/media/CAYAGO_SEABOB_Rack_F9.png",
        grossCents: 35990,
      },
      {
        key: "pilot-belt-system",
        category: "Accessori",
        title: "Pilot Belt System (S, M, L, XL)",
        desc: "Cintura per ridurre l’affaticamento delle braccia durante la guida sportiva ad alta spinta.",
        img: "https://seabob.com/media/pilot-belt-system.png.webp",
        grossCents: 24156,
      },
      {
        key: "seabob-cover",
        category: "Accessori",
        title: "SEABOB Cover",
        desc: "Per proteggere il SEABOB da polvere e luce solare.",
        img: "https://seabob.com/media/seabob-cover-1.webp",
        grossCents: 10248,
      },
      {
        key: "lifting-dock",
        category: "Accessori",
        title: "SEABOB Lifting Dock",
        desc: "Dock gonfiabile per calare o sollevare un SEABOB in/dall’acqua con un davit.",
        img: "https://seabob.com/media/seabob-lifting-dock-1.webp",
        grossCents: 113216,
      },
    ];

    // State accessories become quote-items: { key, category, title, desc, grossCents, checked }
    state.accessories = QUOTE_ITEMS.map((x) => ({ ...x, checked: false }));

    // Shopify product.js variants have option1/2/3
    function variantColor(variant) {
      const idx = CONFIG.colorOptionIndex;
      if (idx === 1) return variant.option1;
      if (idx === 2) return variant.option2;
      if (idx === 3) return variant.option3;
      return variant.option1;
    }

    function pickVariantByColor(product, colorLabel) {
      const want = norm(colorLabel);
      return product.variants.find((v) => norm(variantColor(v)) === want) || null;
    }

    function firstAvailableVariant(product) {
      return product.variants.find((v) => v.available) || product.variants[0] || null;
    }

    function currentModelName() {
      return state.model === "f9s" ? "SEABOB F9S" : "SEABOB F9";
    }

    function whatsappLink() {
      const base = `https://wa.me/${CONFIG.whatsappNumber}`;
      const selectedAcc = state.accessories.filter((a) => a.checked).map((a) => a.title);
      const accText = selectedAcc.length ? `\nAccessori/Optional: ${selectedAcc.join(", ")}` : "";
      const totalText = state.variant ? `\nTotale (IVA incl.): ${money(computeTotalCents())}` : "";
      const text = encodeURIComponent(
        `Ciao! Vorrei un preventivo per ${currentModelName()}\nColore: ${state.colorLabel || "da scegliere"}${accText}${totalText}`
      );
      return `${base}?text=${text}`;
    }

    function computeDiscountCents(baseCents) {
      const unlocked = Date.now() < (state.discount?.unlockedUntil || 0);
      if (!unlocked) return 0;

      const base = Math.max(0, Number(baseCents || 0));
      const val = Math.max(0, Number(state.discount?.value || 0));

      let dc = 0;
      if (state.discount?.mode === "fixed") {
        dc = Math.round(val * 100); // € -> cents
      } else {
        const pct = Math.min(100, val);
        dc = Math.round(base * (pct / 100));
      }

      // clamp: never discount beyond base
      return Math.min(base, Math.max(0, dc));
    }

    function computeTotalCentsOriginal() {
      let total = 0;
      if (state.variant && typeof state.variant.price === "number") total += state.variant.price;
      state.accessories
        .filter((a) => a.checked)
        .forEach((a) => {
          if (typeof a.grossCents === "number") total += a.grossCents;
        });
      return total;
    }

    function computeTotalCents() {
      const base = state.variant && typeof state.variant.price === "number" ? state.variant.price : 0;
      const disc = computeDiscountCents(base);
      const baseAfter = Math.max(0, base - disc);

      let total = baseAfter;
      state.accessories
        .filter((a) => a.checked)
        .forEach((a) => {
          if (typeof a.grossCents === "number") total += a.grossCents;
        });

      return total;
    }

    function initDiscountUI() {
      // PIN provided by Liquid as data-discount-pin
      const pin = (root.dataset.discountPin || "").trim() || "101026";

      const unlockBtn = $("#discountUnlockBtn", root);
      const pinRow = $("#discountPinRow", root);
      const pinInput = $("#discountPinInput", root);
      const pinSubmit = $("#discountPinSubmit", root);
      const msg = $("#discountMsg", root);
      const status = $("#discountStatus", root);
      const locked = $("#discountLocked", root);
      const unlocked = $("#discountUnlocked", root);
      const modeSel = $("#discountMode", root);
      const valInput = $("#discountValue", root);

      // If the section markup isn't present, do nothing
      if (!unlockBtn || !status) return;

      // Restore from sessionStorage (per-tab). Keeps it practical in fiera.
      try {
        const raw = sessionStorage.getItem("pm_discount_state");
        if (raw) {
          const saved = JSON.parse(raw);
          if (saved && typeof saved === "object") {
            state.discount = {
              unlockedUntil: Number(saved.unlockedUntil || 0),
              mode: saved.mode === "fixed" ? "fixed" : "percent",
              value: Number(saved.value || 0),
            };
          }
        }
      } catch (e) {}

      const persist = () => {
        try {
          sessionStorage.setItem(
            "pm_discount_state",
            JSON.stringify({
              unlockedUntil: state.discount?.unlockedUntil || 0,
              mode: state.discount?.mode || "percent",
              value: state.discount?.value || 0,
            })
          );
        } catch (e) {}
      };

      const isUnlocked = () => Date.now() < (state.discount?.unlockedUntil || 0);

      const setUnlockedUI = (ok) => {
        status.textContent = ok ? "Sbloccato" : "Bloccato";
        if (locked) locked.style.display = ok ? "none" : "block";
        if (unlocked) unlocked.style.display = ok ? "block" : "none";
      };

      // initial paint
      setUnlockedUI(isUnlocked());
      if (modeSel) modeSel.value = state.discount?.mode || "percent";
      if (valInput) valInput.value = String(state.discount?.value || 0);

      unlockBtn.addEventListener("click", () => {
        if (pinRow) {
          const isHidden = !pinRow.style.display || pinRow.style.display === "none";
          pinRow.style.display = isHidden ? "flex" : "none";
        }
        if (msg) msg.textContent = "";
        setTimeout(() => {
          try {
            pinInput?.focus();
          } catch (e) {}
        }, 30);
      });

      const doUnlock = () => {
        const entered = String(pinInput?.value || "").trim();
        if (entered !== pin) {
          if (msg) msg.textContent = "PIN non valido.";
          return;
        }

        // Unlock 15 minutes
        state.discount.unlockedUntil = Date.now() + 15 * 60 * 1000;
        if (msg) msg.textContent = "Sconto sbloccato (15 minuti).";
        persist();
        setUnlockedUI(true);
        renderSummary();
      };

      if (pinSubmit) pinSubmit.addEventListener("click", doUnlock);
      if (pinInput)
        pinInput.addEventListener("keydown", (e) => {
          if (e.key === "Enter") doUnlock();
        });

      if (modeSel)
        modeSel.addEventListener("change", () => {
          state.discount.mode = modeSel.value === "fixed" ? "fixed" : "percent";
          persist();
          renderSummary();
        });

      if (valInput)
        valInput.addEventListener("input", () => {
          const n = Number(valInput.value || 0);
          state.discount.value = Number.isFinite(n) ? n : 0;
          persist();
          renderSummary();
        });

      // Auto-expire UI and reset value when expired
      setInterval(() => {
        const ok = isUnlocked();
        setUnlockedUI(ok);
        if (!ok && (state.discount?.value || 0) !== 0) {
          state.discount.value = 0;
          persist();
        }
      }, 30000);
    }

    // =========================
    // RENDER
    // =========================
    function renderModelCards() {
      const pF9 = state.products.f9;
      const pF9S = state.products.f9s;

      const vF9 = pF9 ? firstAvailableVariant(pF9) : null;
      const vF9S = pF9S ? firstAvailableVariant(pF9S) : null;

      const elF9 = root.querySelector('[data-price="f9"]');
      const elF9S = root.querySelector('[data-price="f9s"]');

      if (elF9) elF9.textContent = vF9 ? money(vF9.price) : "—";
      if (elF9S) elF9S.textContent = vF9S ? money(vF9S.price) : "—";

      $$(".modelCard", root).forEach((card) => {
        card.dataset.active = card.dataset.model === state.model ? "true" : "false";
      });
    }

    function swatchDotColor(label) {
      const s = norm(label);
      const map = [
        { keys: ["black", "nero", "matt black", "nero opaco"], color: "#111111" },
        { keys: ["white", "bianco"], color: "#f4f4f4" },
        { keys: ["silver", "argento", "grey", "gray", "grigio"], color: "#b9b9b9" },
        { keys: ["red", "rosso"], color: "#c0392b" },
        { keys: ["orange", "arancio", "arancione"], color: "#d35400" },
        { keys: ["yellow", "giallo"], color: "#f1c40f" },
        { keys: ["blue", "blu", "azzurro"], color: "#2e86de" },
        { keys: ["green", "verde"], color: "#27ae60" },
      ];
      for (const row of map) {
        if (row.keys.some((k) => s.includes(k))) return row.color;
      }
      return null;
    }

    function renderSwatches() {
      const el = $("#swatches", root);
      if (!el || !state.product) return;

      el.innerHTML = "";
      const product = state.product;

      const colors = [];
      product.variants.forEach((v) => {
        const c = variantColor(v);
        if (c && !colors.some((x) => norm(x) === norm(c))) colors.push(c);
      });

      colors.forEach((c) => {
        const b = document.createElement("button");
        b.className = "swatch";
        b.type = "button";
        b.dataset.active = state.colorLabel && norm(state.colorLabel) === norm(c) ? "true" : "false";
        b.innerHTML = `<span class="dot" aria-hidden="true"></span><span class="swatchLabel">${c}</span>`;
        const dot = b.querySelector('.dot');
        const col = swatchDotColor(c);
        if (dot && col) dot.style.background = col;
        b.addEventListener("click", () => selectColor(c));
        el.appendChild(b);
      });
    }

    function renderGallery() {
      const mainImg = $("#mainImg", root);
      const thumbs = $("#thumbs", root);
      if (!mainImg) return;

      // Always hide thumbnails
      if (thumbs) {
        thumbs.innerHTML = "";
        thumbs.style.display = "none";
      }

      const images = state.galleryImages || [];
      if (images.length === 0) {
        mainImg.src = "";
        mainImg.alt = "SEABOB";
        return;
      }

      const src = images[0];
      state.galleryActive = src;
      mainImg.src = src;
      mainImg.alt = `${currentModelName()} ${state.colorLabel ? "- " + state.colorLabel : ""}`;
    }

    function updateProductGalleryFromVariant() {
      const p = state.product;
      if (!p) return;

      // Only one image: variant image preferred, otherwise featured, otherwise first image
      let img = "";
      if (state.variant?.featured_image?.src) img = state.variant.featured_image.src;
      else if (p.featured_image) img = p.featured_image;
      else if (p.images && p.images[0]) img = p.images[0];

      state.galleryImages = img ? [img] : [];
      state.galleryActive = img || null;
      renderGallery();
    }

    function renderAccessories() {
      const loading = $("#accLoading", root);
      const list = $("#accList", root);

      // Hide any loading UI
      if (loading) loading.style.display = "none";
      if (!list) return;

      list.style.display = "flex";
      list.innerHTML = "";

      // Group by category
      const groups = state.accessories.reduce((acc, item) => {
        acc[item.category] = acc[item.category] || [];
        acc[item.category].push(item);
        return acc;
      }, {});

      const order = ["Optional", "Accessori"];
      order.forEach((cat) => {
        const items = groups[cat] || [];
        if (!items.length) return;

        const h = document.createElement("div");
        h.className = "accGroupTitle";
        h.textContent = cat === "Optional" ? "Optional / Equipaggiamento" : "Accessori";
        list.appendChild(h);

        items.forEach((a, idx) => {
          const row = document.createElement("div");
          row.className = "accItem";
          row.classList.toggle("is-selected", !!a.checked);
          row.innerHTML = `
            <div class="accLeft">
              <div class="accImg">${a.img ? `<img src="${a.img}" alt="${a.title}" loading="lazy">` : ""}</div>
              <div class="accMeta">
                <b>${a.title}</b>
                <small>${a.desc}</small>
              </div>
            </div>
            <div class="accRight">
              <div class="priceTag">${money(a.grossCents)}</div>
              <input class="check" type="checkbox" ${a.checked ? "checked" : ""} aria-label="Seleziona ${a.title}">
            </div>
          `;

          const checkbox = row.querySelector(".check");

          // Make the entire row clickable (better UX)
          row.style.cursor = "pointer";
          row.addEventListener("click", (e) => {
            // Avoid double-toggling when clicking directly on the checkbox
            if (e.target && (e.target.classList && e.target.classList.contains("check"))) return;
            checkbox.checked = !checkbox.checked;
            row.classList.toggle("is-selected", checkbox.checked);
            checkbox.dispatchEvent(new Event("change", { bubbles: true }));
          });

          checkbox.addEventListener("change", () => {
            // Find by key (safer than index because of grouping)
            const k = a.key;
            const target = state.accessories.find((x) => x.key === k);
            if (target) target.checked = checkbox.checked;

            row.classList.toggle("is-selected", checkbox.checked);
            renderSummary();
          });

          list.appendChild(row);
        });
      });
    }

    function renderSummary() {
      const model = currentModelName();
      const color = state.colorLabel || "Seleziona un colore";
      const accCount = state.accessories.filter((a) => a.checked).length;

      const originalCents = state.variant ? computeTotalCentsOriginal() : 0;
      const totalCents = state.variant ? computeTotalCents() : 0;
      const total = state.variant ? money(totalCents) : "—";

      const sumModel = $("#sumModel", root);
      const sumColor = $("#sumColor", root);
      const sumAcc = $("#sumAcc", root);
      const sumTotal = $("#sumTotal", root);

      const qsModel = $("#qsModel", root);
      const qsColor = $("#qsColor", root);
      const qsAcc = $("#qsAcc", root);
      const qsTotal = $("#qsTotal", root);

      const bottomPrice = $("#bottomPrice", root);

      if (sumModel) sumModel.textContent = model;
      if (sumColor) sumColor.textContent = color;
      if (sumAcc) sumAcc.textContent = accCount ? `${accCount} selezionati` : "Nessuno";
      if (sumTotal) sumTotal.textContent = total;

      if (qsModel) qsModel.textContent = model;
      if (qsColor) qsColor.textContent = state.colorLabel || "—";
      if (qsAcc) qsAcc.textContent = accCount ? `${accCount}` : "0";
      if (qsTotal) qsTotal.textContent = total;

      if (bottomPrice) bottomPrice.textContent = total;

      // Discount breakdown (only when discount affects total)
      const meta = $("#discountMeta", root);
      const elOrig = $("#sumOriginal", root);
      const elDisc = $("#sumDiscount", root);
      const showMeta = state.variant && totalCents < originalCents;

      if (meta) meta.style.display = showMeta ? "flex" : "none";
      if (showMeta) {
        if (elOrig) elOrig.textContent = money(originalCents);
        if (elDisc) elDisc.textContent = "-" + money(originalCents - totalCents);
      }

      const wa1 = $("#ctaWhatsapp", root);
      const wa2 = $("#ctaWhatsappTop", root);
      const link = whatsappLink();
      if (wa1) wa1.href = link;
      if (wa2) wa2.href = link;
    }

    // =========================
    // ACTIONS
    // =========================
    function selectModel(model) {
      state.model = model;
      state.product = state.products[model];

      const v = firstAvailableVariant(state.product);
      state.variant = v;
      state.variantId = v ? v.id : null;
      state.colorLabel = v ? variantColor(v) : null;

      renderModelCards();
      renderSwatches();
      updateProductGalleryFromVariant();
      renderSummary();
    }

    function selectColor(colorLabel) {
      state.colorLabel = colorLabel;
      const v = pickVariantByColor(state.product, colorLabel);
      state.variant = v;
      state.variantId = v ? v.id : null;

      updateProductGalleryFromVariant();
      renderSwatches();
      renderSummary();
    }

    const pad2 = (n) => String(n).padStart(2, "0");
    const nowStamp = () => {
      const d = new Date();
      return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}-${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`;
    };
    const quoteCode = () => `PM-${nowStamp()}`;

    function printQuote() {
      if (!state.variantId) {
        alert("Seleziona prima un colore.");
        return;
      }

      const code = quoteCode();
      const selectedAcc = state.accessories.filter((a) => a.checked);
      const totalCents = computeTotalCents();

      // WhatsApp link + QR (simple: opens chat with prefilled text; PDF is attached manually by staff)
      const accLines = selectedAcc.length
        ? selectedAcc
            .slice(0, 12)
            .map((a) => `- ${a.title}`)
            .join("\n")
        : "- Nessuno";

      const originalCents = computeTotalCentsOriginal();
      const hasDiscount = totalCents < originalCents;

      const waText = encodeURIComponent(
        `Ciao! Ti invio i dettagli del preventivo (PDF in allegato).\n` +
          `Codice: ${code}\n` +
          `Modello: ${currentModelName()}\n` +
          `Colore: ${state.colorLabel || "da scegliere"}\n` +
          `Optional/Accessori:\n${accLines}\n` +
          (hasDiscount
            ? `Totale: ${money(originalCents)}\nSconto: -${money(originalCents - totalCents)}\nTotale scontato: ${money(totalCents)}\n`
            : `Totale: ${money(totalCents)}\n`) +
          `Validità fiera: fino al 15/02/2026.`
      );

      const waHref = `https://wa.me/${CONFIG.whatsappNumber}?text=${waText}`;
      const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(waHref)}`;

      const baseCents = state.variant?.price || 0;
      const discCents = computeDiscountCents(baseCents);
      const baseAfterCents = Math.max(0, baseCents - discCents);

      // Brand / company
      const LOGO_URL = "https://passionmarinenapoli.com/cdn/shop/files/PASSION_ORO_LUCIDO_3_48118ea1-0db4-4f71-a7c7-0b0d83e6e48a.png?v=1736436793&width=700";
      const COMPANY = {
        name: "Passion Marine Napoli",
        site: "passionmarinenapoli.com",
        whatsapp: "+39 342 348 8673",
      };

      const modelRow = `
        <tr>
          <td>
            <b>${currentModelName()}</b>
            <div class="muted2">Colore: ${state.colorLabel || "—"}</div>
          </td>
          <td>
            <div class="muted2">Unità principale</div>
          </td>
          <td style="text-align:right"><b>${money(baseAfterCents)}</b></td>
        </tr>
      `;

      const discountRow =
        discCents > 0
          ? `
        <tr>
          <td>
            <b>Sconto fiera</b>
            <div class="muted2">Applicato solo al prezzo base SEABOB</div>
          </td>
          <td>
            <div class="pill2">Sconto</div>
          </td>
          <td style="text-align:right"><b>-${money(discCents)}</b></td>
        </tr>
      `
          : "";

      const accRows = selectedAcc
        .map(
          (a) => `
          <tr>
            <td>
              <b>${a.title}</b>
              ${a.desc ? `<div class="muted2">${a.desc}</div>` : ""}
            </td>
            <td>
              <div class="pill2">${a.category}</div>
            </td>
            <td style="text-align:right">${money(a.grossCents)}</td>
          </tr>
        `
        )
        .join("");

      const html = `
<!doctype html>
<html lang="it">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>Preventivo SEABOB | ${COMPANY.name}</title>
  <style>
    :root{ --line:#e9e9e9; --muted:#666; }
    body{ font-family: Arial, Helvetica, sans-serif; padding:24px; color:#111; }
    .top{ display:flex; justify-content:space-between; gap:16px; align-items:flex-start; }
    .rightCol{ display:flex; flex-direction:column; align-items:flex-end; gap:12px; }
    .qrBox{ display:flex; flex-direction:column; align-items:center; gap:6px; padding:10px; border:1px solid var(--line); border-radius:12px; background:#fff; }
    .qrLabel{ font-size:12px; color:var(--muted); }
    .qrImg{ width:180px; height:180px; object-fit:contain; display:block; }
    .qrHint{ font-size:12px; color:var(--muted); }
    .brand{ display:flex; gap:12px; align-items:flex-start; }
    .logo{ width:140px; height:auto; }
    h1{ margin:0 0 6px; font-size:20px; }
    .muted{ color:var(--muted); font-size:12px; line-height:1.4; }
    .muted2{ color:var(--muted); font-size:12px; line-height:1.35; margin-top:4px; }
    .meta{ display:flex; gap:10px; flex-wrap:wrap; margin-top:6px; }
    .tag{ border:1px solid var(--line); border-radius:999px; padding:6px 10px; font-size:12px; color:var(--muted); }
    table{ width:100%; border-collapse:collapse; margin-top:14px; }
    th,td{ border-bottom:1px solid var(--line); padding:10px 8px; font-size:13px; vertical-align:top; }
    th{ text-align:left; background:#fafafa; font-size:12px; text-transform:uppercase; letter-spacing:.02em; color:var(--muted); }
    .total{ margin-top:14px; display:flex; justify-content:flex-end; }
    .totalBox{ border:1px solid var(--line); padding:10px 12px; border-radius:10px; }
    .totalBox b{ font-size:18px; }
    .pill2{ display:inline-block; border:1px solid var(--line); border-radius:999px; padding:4px 8px; font-size:12px; color:var(--muted); }
    .space{ height:10px; }

    @media print{
      body{ padding:16px; }
      .tag{ border-color:#ddd; }
      th{ -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="top">
    <div>
      <div class="brand">
        <img class="logo" src="${LOGO_URL}" alt="${COMPANY.name}" />
        <div>
          <h1>Preventivo SEABOB</h1>
          <div class="muted">${COMPANY.name}</div>
          <div class="muted">Sito: ${COMPANY.site} · WhatsApp: ${COMPANY.whatsapp}</div>
        </div>
      </div>
      <div class="meta">
        <div class="tag">Codice: <b>${code}</b></div>
        <div class="tag">Data: <b>${todayStr()}</b></div>
        <div class="tag">Prezzi: <b>IVA inclusa</b></div>
      </div>
    </div>

    <div class="rightCol">
      <div class="muted" style="text-align:right">
        Cliente: _______________________<br/>
        Telefono: ______________________<br/>
        Email: _________________________
      </div>

      <div class="qrBox">
        <div class="qrLabel">Scansiona per WhatsApp</div>
        <img class="qrImg" src="${qrSrc}" alt="QR WhatsApp" />
        <div class="qrHint">Preventivo: <b>${code}</b></div>
      </div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Articolo</th>
        <th>Categoria</th>
        <th style="text-align:right">Prezzo (IVA incl.)</th>
      </tr>
    </thead>
    <tbody>
      ${modelRow}
      ${discountRow}
      ${accRows}
    </tbody>
  </table>

  <div class="total">
    <div class="totalBox">Totale (IVA incl.): <b>${money(totalCents)}</b></div>
  </div>

  <div class="space"></div>
  <div class="muted">Nota: prezzi indicativi soggetti a disponibilità e configurazione finale. Per conferma e tempi, contattaci su WhatsApp indicando il codice preventivo (oppure scansiona il QR).</div>
</body>
</html>`;

      // Print via hidden iframe (avoids popup blockers)
      let iframe = document.getElementById("pmQuotePrintFrame");
      if (!iframe) {
        iframe = document.createElement("iframe");
        iframe.id = "pmQuotePrintFrame";
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        document.body.appendChild(iframe);
      }

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();

      const tryPrint = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error(e);
          alert("Impossibile avviare la stampa. Controlla le impostazioni del browser.");
        }
      };

      // Wait for images (logo) to be ready before printing (best-effort)
      const waitImagesThenPrint = () => {
        const imgs = Array.from(iframe.contentWindow.document.images || []);
        if (!imgs.length) return tryPrint();
        const pending = imgs.filter((im) => !im.complete);
        if (!pending.length) return tryPrint();

        let left = pending.length;
        const done = () => {
          left -= 1;
          if (left <= 0) tryPrint();
        };
        pending.forEach((im) => {
          im.addEventListener("load", done, { once: true });
          im.addEventListener("error", done, { once: true });
        });

        // Fallback timeout
        setTimeout(tryPrint, 800);
      };

      // Some browsers don't fire onload reliably after document.write
      setTimeout(waitImagesThenPrint, 250);
    }

    // =========================
    // WIRE EVENTS
    // =========================
    const btnAdd = $("#ctaAdd", root);
    if (btnAdd) {
      btnAdd.textContent = "Stampa preventivo";
      btnAdd.addEventListener("click", printQuote);
    }

    const btnAddTop = $("#ctaAddTop", root);
    if (btnAddTop) {
      btnAddTop.textContent = "Stampa preventivo";
      btnAddTop.addEventListener("click", printQuote);
    }

    const btnAddBottom = $("#ctaAddBottom", root);
    if (btnAddBottom) {
      btnAddBottom.textContent = "Stampa preventivo";
      btnAddBottom.addEventListener("click", printQuote);
    }

    $$(".modelCard", root).forEach((card) => {
      const model = card.dataset.model;
      card.addEventListener("click", () => selectModel(model));
      card.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") selectModel(model);
      });
    });

    // =========================
    // LOAD DATA
    // =========================
    try {
      const [pF9, pF9S] = await Promise.all([
        fetchProduct(CONFIG.f9Handle),
        fetchProduct(CONFIG.f9sHandle),
      ]);
      state.products.f9 = pF9;
      state.products.f9s = pF9S;

      renderModelCards();

      renderAccessories();
      initDiscountUI();
      selectModel("f9");
    } catch (err) {
      console.error(err);
      alert("Errore caricamento configuratore. Controlla gli handle prodotto in sezione.");
    }
  });
})();